import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  Paper,
} from '@mui/material';
import {
  AutoFixHigh as OptimizeIcon,
  CheckCircle as AcceptIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { api } from '../api';

interface PromptOptimizationModalProps {
  open: boolean;
  originalPrompt: string;
  onClose: () => void;
  onAccept: (optimizedPrompt: string) => void;
}

const PromptOptimizationModal: React.FC<PromptOptimizationModalProps> = ({
  open,
  originalPrompt,
  onClose,
  onAccept,
}) => {
  const [optimizedPrompt, setOptimizedPrompt] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [modelUsed, setModelUsed] = useState<string>('');

  // Trigger optimization when modal opens
  useEffect(() => {
    if (open && originalPrompt) {
      optimizePrompt();
    }
  }, [open, originalPrompt]);

  const optimizePrompt = async () => {
    setLoading(true);
    setError(null);
    setOptimizedPrompt('');

    try {
      const result = await api.optimizePrompt(originalPrompt);
      setOptimizedPrompt(result.optimizedPrompt);
      setModelUsed(result.model);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to optimize prompt');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = () => {
    if (optimizedPrompt) {
      onAccept(optimizedPrompt);
      handleClose();
    }
  };

  const handleClose = () => {
    setOptimizedPrompt('');
    setError(null);
    setModelUsed('');
    onClose();
  };

  const handleRetry = () => {
    optimizePrompt();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <OptimizeIcon color="primary" />
        <Typography variant="h6">Prompt Optimization</Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, mt: 1 }}>
          {/* Original Prompt */}
          <Box sx={{ flex: 1 }}>
            <Paper elevation={2} sx={{ p: 2, backgroundColor: '#f5f5f5', height: '100%' }}>
              <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" gutterBottom>
                ORIGINAL PROMPT
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <TextField
                multiline
                fullWidth
                value={originalPrompt}
                InputProps={{
                  readOnly: true,
                }}
                variant="outlined"
                rows={12}
                sx={{
                  '& .MuiInputBase-root': {
                    backgroundColor: 'white',
                  },
                  '& textarea': {
                    overflowY: 'auto !important',
                    maxHeight: '400px',
                    resize: 'vertical',
                  }
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Character count: {originalPrompt.length}
              </Typography>
            </Paper>
          </Box>

          {/* Optimized Prompt */}
          <Box sx={{ flex: 1 }}>
            <Paper 
              elevation={2} 
              sx={{ 
                p: 2, 
                backgroundColor: loading ? '#fff' : '#e8f5e9',
                border: loading ? 'none' : '2px solid #4CAF50',
                height: '100%'
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" gutterBottom>
                OPTIMIZED PROMPT
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {loading && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                  <CircularProgress size={60} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Optimizing your prompt...
                  </Typography>
                </Box>
              )}

              {error && !loading && (
                <Alert 
                  severity="error" 
                  sx={{ mb: 2 }}
                  action={
                    <Button size="small" onClick={handleRetry}>
                      Retry
                    </Button>
                  }
                >
                  {error}
                </Alert>
              )}

              {optimizedPrompt && !loading && (
                <>
                  <TextField
                    multiline
                    fullWidth
                    value={optimizedPrompt}
                    InputProps={{
                      readOnly: true,
                    }}
                    variant="outlined"
                    rows={12}
                    sx={{
                      '& .MuiInputBase-root': {
                        backgroundColor: 'white',
                      },
                      '& textarea': {
                        overflowY: 'auto !important',
                        maxHeight: '400px',
                        resize: 'vertical',
                      }
                    }}
                  />
                  <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      Character count: {optimizedPrompt.length}
                    </Typography>
                    {modelUsed && (
                      <Typography variant="caption" color="primary" fontWeight="medium">
                        Optimized by: {modelUsed}
                      </Typography>
                    )}
                  </Box>
                </>
              )}
            </Paper>
          </Box>
        </Box>

        {/* Comparison Summary */}
        {optimizedPrompt && !loading && (
          <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="caption" fontWeight="bold" color="text.secondary">
              SUMMARY
            </Typography>
            <Box sx={{ mt: 1, display: 'flex', gap: 3 }}>
              <Typography variant="body2">
                Original: <strong>{originalPrompt.length} characters</strong>
              </Typography>
              <Typography variant="body2">
                Optimized: <strong>{optimizedPrompt.length} characters</strong>
              </Typography>
              <Typography variant="body2" color={optimizedPrompt.length > originalPrompt.length ? 'success.main' : 'info.main'}>
                Change: <strong>
                  {optimizedPrompt.length > originalPrompt.length ? '+' : ''}
                  {optimizedPrompt.length - originalPrompt.length} characters
                </strong>
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          startIcon={<CloseIcon />}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleAccept}
          startIcon={<AcceptIcon />}
          variant="contained"
          disabled={!optimizedPrompt || loading}
          color="success"
        >
          Accept Optimized Prompt
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PromptOptimizationModal;



